import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { FieldProps, useRecordContext } from 'react-admin';

const GET_SIGNED_URL = gql`
  query GetSignedUrl($key: String!) {
    get_signed_url(key: $key) {
      url
    }
  }
`;

interface SignedUrlResponse {
  get_signed_url: {
    url: string;
  };
}

interface RecordType {
  [key: string]: any;
}

interface CloudFrontImageFieldProps extends FieldProps {
  source: string;
  record?: RecordType;
}

const CloudFrontImageField: React.FC<CloudFrontImageFieldProps> = ({ source, record: recordProp }) => {
  const recordContext = useRecordContext<RecordType>();
  const record = recordProp || recordContext;
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const key = source !== 'request' ? record?.[source] : record?.request?.imageKey;

  const { data, error } = useQuery<SignedUrlResponse>(GET_SIGNED_URL, {
    variables: { key },
    skip: !record || !key,
  });

  useEffect(() => {
    if (data) {
      setSignedUrl(data.get_signed_url.url);
      setImageError(false);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching signed URL:', error);
      setImageError(true);
    }
  }, [error]);

  if (!record) return null;

  const handleImageClick = () => {
    if (signedUrl && !imageError) {
      window.open(signedUrl, '_blank');
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const defaultImageUrl =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y0ZjRmNCIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4=';

  return (
    <img
      src={imageError || !signedUrl ? defaultImageUrl : signedUrl}
      alt={record[source]}
      title={record[source]}
      style={{ maxWidth: '100%', maxHeight: '200px', cursor: imageError ? 'default' : 'pointer' }}
      onClick={handleImageClick}
      onError={handleImageError}
    />
  );
};

export default CloudFrontImageField;
